import moment from 'moment';
import { API } from "aws-amplify";
import { clamp, ONE_DAY_IN_MILLISECONDS, tidyFlowVolume, tidySvcVolumeTime } from "./dataLib";
import { getSeries } from "./seriesLib";
import "../Polyfills/arrayAtPolyfill";

export async function createFvcChart(tidiedPoints, type, size = { width: 350, height: 300 }) {
  // types - flow volume, volume time
  if (typeof (tidiedPoints.flowVolumeCurve) === 'string') tidiedPoints = tidyFlowVolume(tidiedPoints, true);
  const typeObject = {
    label: type === 'flowVolume' ? 'Flow Volume Curve' : 'Volume Time Curve',
    xAxisLabel: type === 'flowVolume' ? 'Volume (L)' : 'Time (s)',
    yAxisLabel: type === 'flowVolume' ? 'Flow (L/s)' : 'Volume (L)',
  }
  const body = {
    chartType: 'singlePftChart',
    chartSize: {
      width: size.width,
      height: size.height
    },
    ticks: {
      xAxis: {
        stepSize: type === 'flowVolume' ? tidiedPoints.fvcStepSize.xStepSize : tidiedPoints.vtcStepSize.xStepSize,
        min: type === 'flowVolume' ? tidiedPoints.ranges.volumeRange[0] : tidiedPoints.ranges.timeRange[0],
        max: type === 'flowVolume' ? tidiedPoints.ranges.volumeRange[1] : tidiedPoints.ranges.timeRange[1],
        label: typeObject.xAxisLabel
      },
      yAxis: {
        stepSize: type === 'flowVolume' ? tidiedPoints.fvcStepSize.yStepSize : tidiedPoints.vtcStepSize.yStepSize,
        min: type === 'flowVolume' ? tidiedPoints.ranges.flowRange[0] : tidiedPoints.ranges.volumeRange[0],
        max: type === 'flowVolume' ? tidiedPoints.ranges.flowRange[1] : tidiedPoints.ranges.volumeRange[1],
        label: typeObject.yAxisLabel
      }
    },
    points: type === 'flowVolume' ? tidiedPoints.xyFlowVolume : tidiedPoints.xyVolumeTime,
  }
  let response = await API.post("chartGenerator", `/new-chart`, {
    body: body
  });
  return response;
}

export async function createSvcChart(tidiedPoints, type, size = { width: 350, height: 300 }) {
  if (typeof (tidiedPoints.flowVolumeCurve) === 'string') tidiedPoints = tidySvcVolumeTime(tidiedPoints, true);
  const typeObject = {
    label: 'Volume Time Curve',
    xAxisLabel: 'Time (s)',
    yAxisLabel: 'Volume (L)',
  }
  const body = {
    chartType: 'singlePftChart',
    chartSize: {
      width: size.width,
      height: size.height
    },
    ticks: {
      xAxis: {
        stepSize: tidiedPoints.vcStepSize.xStepSize,
        min: tidiedPoints.ranges.timeRange[0],
        max: tidiedPoints.ranges.timeRange[1],
        label: typeObject.xAxisLabel
      },
      yAxis: {
        stepSize: tidiedPoints.vcStepSize.yStepSize,
        min: tidiedPoints.ranges.volumeRange[0],
        max: tidiedPoints.ranges.volumeRange[1],
        label: typeObject.yAxisLabel
      }
    },
    points: tidiedPoints.xyVolumeTime,
  }
  let response = await API.post("chartGenerator", `/new-chart`, {
    body: body
  });
  return response;
}

export async function createTrendChart(tidiedPoints, type, size = { width: 600, height: 300 }) {
  // if (typeof(tidiedPoints.flowVolumeCurve) === 'string') tidiedPoints = tidySvcVolumeTime(tidiedPoints, true);
  // const typeObject = {
  //   label: 'FVC Trend Chart',
  //   xAxisLabel: 'Date',
  //   yAxisLabel: 'Volume (L)',
  // }
  const body = {
    chartType: 'fvcTrendChart',
    chartSize: {
      width: size.width,
      height: size.height
    },
    // ticks: {
    //   xAxis: {
    //     stepSize: tidiedPoints.vcStepSize.xStepSize,
    //     min: tidiedPoints.ranges.timeRange[0],
    //     max: tidiedPoints.ranges.timeRange[1],
    //     label: typeObject.xAxisLabel
    //   },
    //   yAxis: {
    //     stepSize: tidiedPoints.vcStepSize.yStepSize,
    //     min: tidiedPoints.ranges.volumeRange[0],
    //     max: tidiedPoints.ranges.volumeRange[1],
    //     label: typeObject.yAxisLabel
    //   }
    // },
    points: tidiedPoints,
  }
  let response = await API.post("chartGenerator", `/new-chart`, {
    body: body
  });
  return response;
};

export const formatChartDataForPatientGraph = async (chart, pftType = 'fvc', params, patient, authenticatedProvider) => {
  const newChart = {
    fvc: {
      chart: Object.values(chart.fvc.chart).sort((a, b) => a.timeslot - b.timeslot),
      stats: chart.fvc.stats,
      max: chart.fvc.max
    },
    svc: {
      chart: Object.values(chart.svc.chart).sort((a, b) => a.timeslot - b.timeslot),
      stats: chart.svc.stats,
      max: chart.svc.max
    },
    pef: {
      chart: Object.values(chart.pef?.chart ?? {}).sort((a, b) => a.timeslot - b.timeslot),
      stats: chart.pef?.stats,
      max: chart.pef?.max
    },
    incentiveSpirometry: {
      chart: chart.incentiveSpirometry.chart,
      stats: chart.incentiveSpirometry.stats,
      max: chart.incentiveSpirometry.max
    }
  };

  const coachedFvcs = Object.values(newChart.fvc.stats).filter(fvc => newChart.fvc.stats[fvc.seriesID]?.sessionID);
  const coachedSvcs = Object.values(newChart.svc.stats).filter(svc => newChart.svc.stats[svc.seriesID]?.sessionID);
  let coachedPefs;
  if (newChart.pef.stats) {
    coachedPefs = Object.values(newChart.pef.stats).filter(pef => newChart.pef.stats[pef.seriesID]?.sessionID);
  }

  // ADDING EMPTY TIMESLOTS FOR DAYS WITHOUT TESTS TO FILL OUT OUR SVC CHART
  if (newChart.svc.chart.length > 1) {
    let finalSvcChart = [newChart.svc.chart[0]];
    for (let i = 1; i < newChart.svc.chart.length; i += 1) {
      let difference = ONE_DAY_IN_MILLISECONDS;
      while ((newChart.svc.chart[i - 1].timeslot + difference) < newChart.svc.chart[i].timeslot) {
        finalSvcChart.push({ timeslot: newChart.svc.chart[i - 1].timeslot + difference })
        difference += ONE_DAY_IN_MILLISECONDS;
      }
      finalSvcChart.push(newChart.svc.chart[i])
    }
    newChart.svc.chart = finalSvcChart;
  };
  // FOR COACHED SVC'S NOW
  if (coachedSvcs.length > 1) {
    let finalCoachedSvcChart = [coachedSvcs[0]];
    for (let i = 1; i < coachedSvcs.length; i += 1) {
      let difference = ONE_DAY_IN_MILLISECONDS;
      while ((coachedSvcs[i - 1].timeslot + difference) < coachedSvcs[i].timeslot) {
        finalCoachedSvcChart.push({ timeslot: coachedSvcs[i - 1].timeslot + difference })
        difference += ONE_DAY_IN_MILLISECONDS;
      }
      finalCoachedSvcChart.push(coachedSvcs[i])
    }
    newChart.svc.coachedChart = finalCoachedSvcChart;

    // CREATE A MAP FOR COACHED SERIES W/ BEST SVC PER TIMESLOT
    const validCoachedSvcChartEntries = newChart.svc.coachedChart.filter(entry => !!entry.seriesID);
    const coachedSvcStatsByTimeslot = validCoachedSvcChartEntries.reduce((acc, currentEntry) => {
      const existingEntry = acc[currentEntry.timeslot];
      if (existingEntry && existingEntry.vc > currentEntry.vc) return { ...acc };
      return { ...acc, [currentEntry.timeslot]: currentEntry };
    }, {});
    newChart.svc.bestCoachedStatsByTimeslot = coachedSvcStatsByTimeslot;
  };

  // ADDING EMPTY TIMESLOTS FOR DAYS WITHOUT TESTS TO FILL OUT OUR FVC CHART
  if (newChart.fvc.chart.length > 1) {
    let finalFvcChart = [newChart.fvc.chart[0]];
    for (let i = 1; i < newChart.fvc.chart.length; i += 1) {
      let difference = ONE_DAY_IN_MILLISECONDS;
      while ((newChart.fvc.chart[i - 1].timeslot + difference) < newChart.fvc.chart[i].timeslot) {
        finalFvcChart.push({ timeslot: newChart.fvc.chart[i - 1].timeslot + difference })
        difference += ONE_DAY_IN_MILLISECONDS;
      }
      finalFvcChart.push(newChart.fvc.chart[i])
    }
    newChart.fvc.chart = finalFvcChart;
  };
  // For Coached FVC's now
  if (coachedFvcs.length > 1) {
    let finalCoachedFvcChart = [coachedFvcs[0]];
    for (let i = 1; i < coachedFvcs.length; i += 1) {
      let difference = ONE_DAY_IN_MILLISECONDS;
      while ((coachedFvcs[i - 1].timeslot + difference) < coachedFvcs[i].timeslot) {
        finalCoachedFvcChart.push({ timeslot: coachedFvcs[i - 1].timeslot + difference })
        difference += ONE_DAY_IN_MILLISECONDS;
      }
      finalCoachedFvcChart.push(coachedFvcs[i])
    };
    newChart.fvc.coachedChart = finalCoachedFvcChart;

    // CREATE A MAP FOR COACHED SERIES W/ BEST FVC PER TIMESLOT
    const validCoachedFvcChartEntries = newChart.fvc.coachedChart.filter(entry => !!entry.seriesID);
    const coachedFvcStatsByTimeslot = validCoachedFvcChartEntries.reduce((acc, currentEntry) => {
      const existingEntry = acc[currentEntry.timeslot];
      if (existingEntry && existingEntry.fvc > currentEntry.fvc) return { ...acc };
      return { ...acc, [currentEntry.timeslot]: currentEntry };
    }, {});
    newChart.fvc.bestCoachedStatsByTimeslot = coachedFvcStatsByTimeslot;
  };

  // ADDING EMPTY TIMESLOTS FOR DAYS WITHOUT TESTS TO FILL OUT OUR PEF CHART
  if (newChart.pef.chart.length > 1) {
    let finalPefChart = [newChart.pef.chart[0]];
    for (let i = 1; i < newChart.pef.chart.length; i += 1) {
      let difference = ONE_DAY_IN_MILLISECONDS;
      while ((newChart.pef.chart[i - 1].timeslot + difference) < newChart.pef.chart[i].timeslot) {
        finalPefChart.push({ timeslot: newChart.pef.chart[i - 1].timeslot + difference })
        difference += ONE_DAY_IN_MILLISECONDS;
      }
      finalPefChart.push(newChart.pef.chart[i])
    }
    newChart.pef.chart = finalPefChart;
  };
  // For Coached PEF's now
  if (coachedPefs && coachedPefs.length > 1) {
    let finalCoachedPefChart = [coachedPefs[0]];
    for (let i = 1; i < coachedPefs.length; i += 1) {
      let difference = ONE_DAY_IN_MILLISECONDS;
      while ((coachedPefs[i - 1].timeslot + difference) < coachedPefs[i].timeslot) {
        finalCoachedPefChart.push({ timeslot: coachedPefs[i - 1].timeslot + difference })
        difference += ONE_DAY_IN_MILLISECONDS;
      }
      finalCoachedPefChart.push(coachedPefs[i])
    };
    newChart.pef.coachedChart = finalCoachedPefChart;

    // CREATE A MAP FOR COACHED SERIES W/ BEST PEF PER TIMESLOT
    const validCoachedPefChartEntries = newChart.pef.coachedChart.filter(entry => !!entry.seriesID);
    const coachedPefStatsByTimeslot = validCoachedPefChartEntries.reduce((acc, currentEntry) => {
      const existingEntry = acc[currentEntry.timeslot];
      if (existingEntry && existingEntry.pef > currentEntry.pef) return { ...acc };
      return { ...acc, [currentEntry.timeslot]: currentEntry };
    }, {});
    newChart.pef.bestCoachedStatsByTimeslot = coachedPefStatsByTimeslot;
  };

  let series = null, stats = null, isValidSeriesID = false;
  // IF WE HAVE A SERIES ID IN THE URL...
  if (params.seriesID) {
    try {
      // GET THE SERIES WITH THE SERIES ID IN THE URL PATH FOR THIS PATIENT
      let propsSeries = await getSeries(params.seriesID, patient?.patientID ?? params.id);
      if (propsSeries) {
        // FIND AND SET THE SERIES AND STATS TO THAT SERIES FOR FVC
        if (pftType === 'fvc') {
          for (let i = newChart.fvc.chart.length - 1; i >= 0; i -= 1) {
            if (!newChart.fvc.chart[i].seriesID || !(newChart.fvc.chart[i].seriesID === params.seriesID || newChart.fvc.chart[i].otherTests.some(series => series.seriesID === params.seriesID))) continue;

            let seriesDetails;
            for (let j = 0; j < newChart.fvc.chart[i].otherTests.length; j += 1) {
              if (newChart.fvc.chart[i].otherTests[j].seriesID !== params.seriesID) continue;

              seriesDetails = newChart.fvc.chart[i].otherTests[j];
              break;
            };

            series = seriesDetails;
            stats = newChart.fvc.stats[seriesDetails.seriesID];
            isValidSeriesID = newChart.fvc.stats[seriesDetails.seriesID].created;
            break;
          }
        } else if (pftType === 'svc') { // FIND AND SET THE SERIES AND STATS TO THAT SERIES FOR SVC
          for (let i = newChart.svc.chart.length - 1; i >= 0; i -= 1) {
            if (!newChart.svc.chart[i].seriesID || !(newChart.svc.chart[i].seriesID === params.seriesID || newChart.svc.chart[i].otherTests.some(series => series.seriesID === params.seriesID))) continue;

            // setPftType('svc');
            let seriesDetails;
            for (let j = 0; j < newChart.svc.chart[i].otherTests.length; j += 1) {
              if (newChart.svc.chart[i].otherTests[j].seriesID !== params.seriesID) continue;

              seriesDetails = newChart.svc.chart[i].otherTests[j]
              break;
            }
            series = seriesDetails;
            stats = newChart.svc.stats[seriesDetails.seriesID];
            isValidSeriesID = newChart.svc.stats[seriesDetails.seriesID].created;
            break;
          }
        } else if (pftType === 'pef') { // FIND AND SET THE SERIES AND STATS TO THAT SERIES FOR PEF
          for (let i = newChart.pef.chart.length - 1; i >= 0; i -= 1) {
            if (!newChart.pef.chart[i].seriesID || !(newChart.pef.chart[i].seriesID === params.seriesID || newChart.pef.chart[i].otherTests.some(series => series.seriesID === params.seriesID))) continue;

            let seriesDetails;
            for (let j = 0; j < newChart.pef.chart[i].otherTests.length; j += 1) {
              if (newChart.pef.chart[i].otherTests[j].seriesID !== params.seriesID) continue;

              seriesDetails = newChart.pef.chart[i].otherTests[j]
              break;
            }
            series = seriesDetails;
            stats = newChart.pef.stats[seriesDetails.seriesID];
            isValidSeriesID = newChart.pef.stats[seriesDetails.seriesID].created;
            break;
          }
        }
      }
    } catch (e) {
      console.log("not a valid seriesID: ", e);
    }
  } else { // NO SERIES ID IN THE URL PATH PARAMS - SET THE SERIES AND STATS AS MOST RECENT VALID OPTION
    if (pftType === 'fvc') {
      for (let i = newChart.fvc.chart.length - 1; i >= 0; i -= 1) {
        if (newChart.fvc.chart[i].seriesID === null || newChart.fvc.chart[i].fvc === 0) continue;

        series = newChart.fvc.chart[i];
        stats = newChart.fvc.stats[newChart.fvc.chart[i].seriesID];
        break;
      }
    } else if (pftType === 'svc') {
      for (let i = newChart.svc.chart.length - 1; i >= 0; i -= 1) {
        if (newChart.svc.chart[i].seriesID === null || newChart.svc.chart[i].svc === 0) continue;

        series = newChart.svc.chart[i];
        stats = newChart.svc.stats[newChart.svc.chart[i].seriesID];
        break;
      }
    } else if (pftType === 'pef') {
      for (let i = newChart.pef.chart.length - 1; i >= 0; i -= 1) {
        if (newChart.pef.chart[i].seriesID === null || newChart.pef.chart[i].pef === 0) continue;

        series = newChart.pef.chart[i];
        stats = newChart.pef.stats[newChart.pef.chart[i].seriesID];
        break;
      }
    }
    // INFO: FUTURE ADDITIONS WILL GO HERE
  };

  return { series, stats, isValidSeriesID, newChart };
};

/**
 * Integer tick array spanning [min, max] at the given step. Shared by the BD
 * overlay charts so the pre/post curves render on one set of axis ticks.
 */
export const mergedTicks = (min, max, step) => {
  const ticks = [];
  for (let v = Math.floor(min); v <= Math.ceil(max); v += step) ticks.push(v);
  return ticks;
};

/**
 * @param {*} series - FVC Series
 */
export const tidyVolumeTimeCurve = (series, { timeLeadIn = true } = {}) => {
  if (!series?.flowVolumeCurve) return [];

  let backendFlowVolumeCurveData;
  try {
    backendFlowVolumeCurveData = JSON.parse(atob(series.flowVolumeCurve));
  } catch {
    console.log("Unable to parse backend flow volume curve data")
  };

  let containsVtPointsCurve = !!series?.vtPointsCurve;
  const decodedVtPointsCurve = containsVtPointsCurve ? atob(series.vtPointsCurve) : atob(series.flowVolumeCurve);

  let vtPointsJson;
  try {
    vtPointsJson = JSON.parse(decodedVtPointsCurve);
  } catch {
    console.log('ERROR PARSING COMPRESSED CURVE JSON - ATTEMPTING FALLBACK');
    containsVtPointsCurve = false;
    try {
      vtPointsJson = backendFlowVolumeCurveData;
    } catch (e) {
      console.log(e);
      return [];
    }
  };

  // Copy the MIR data to our conventionally named properties for vtPointsCurve
  if (containsVtPointsCurve) {
    vtPointsJson.points.forEach(point => {
      point.volume = point.volume_L
      point.timestamp = point.time_s
    });
  };

  if (!vtPointsJson?.points || vtPointsJson?.points?.length <= 0) return [];

  const volumeTimePoints = [{ volume: 0, timestamp: 0 }];

  if (containsVtPointsCurve) {
    vtPointsJson.points.forEach(point => {
      const { volume, timestamp } = point;
      if (volume > 20) return; // SOME SPIROMETERS RETURN REALLY LARGE VALUES (e.g. 665 volume) WE NEED TO SKIP THESE LARGE INCORRECT VALUES
      volumeTimePoints.push({ volume, timestamp });
    });
  } else { // DIFFERENT CLEANING LOGIC IF NOT A vtPointsCurve
    let firstTimestampWithVolumePresent = undefined;
    vtPointsJson.points.forEach(point => {
      const { volume, timestamp } = point;
      const shouldIgnorePoint = (volume === 0 && volumeTimePoints.at(-1).volume === 0) || (volume < volumeTimePoints.at(-1).volume * 1000) || (firstTimestampWithVolumePresent && ((timestamp - firstTimestampWithVolumePresent) / 1000) > series?.fet);;
      if (shouldIgnorePoint) return;

      if (!firstTimestampWithVolumePresent) firstTimestampWithVolumePresent = timestamp;
      volumeTimePoints.push({
        volume: volume / 1000, // OUR DATA, UNLIKE MIR'S COMES TO US IN ML & MS
        timestamp: (timestamp - firstTimestampWithVolumePresent) / 1000 // WE NEED TO CONVERT TO SECONDS & LITERS
      });
    });
  };

  // MAKE SURE VOLUME TIME POINTS REACH THE FET
  if (series?.fet && volumeTimePoints.at(-1).timestamp < series.fet) {
    const { volume } = volumeTimePoints.at(-1);
    volumeTimePoints.push({ volume, timestamp: series.fet });
  };

  // GENERATE TIME TICKS FOR X-AXIS
  let timeTicks = [];
  for (let i = 0; i <= Math.ceil(volumeTimePoints[volumeTimePoints.length - 1].timestamp) + 1; i += 1) {
    timeTicks.push(i);
  };

  // CREATE VOLUME TICKS FOR Y-AXIS
  let volumeTicks = [];

  for (let i = 0; i <= Math.floor(volumeTimePoints.at(-1).volume) + 2; i += 1) {
    volumeTicks.push(i);
  };

  // SAME TICKS FOR EACH AXIS
  if (volumeTicks.length !== timeTicks.length) {
    if (volumeTicks.length > timeTicks.length) {
      // add to time ticks
      let tickDifference = volumeTicks.length - timeTicks.length;
      for (let i = 0; i < tickDifference; i += 1) {
        let endingTick = timeTicks.at(-1);
        timeTicks.push(endingTick + 1);
      };
    } else if (volumeTicks.length < timeTicks.length) {
      // add to volume ticks
      let tickDifference = timeTicks.length - volumeTicks.length;
      for (let i = 0; i < tickDifference; i += 1) {
        let endingTick = volumeTicks.at(-1);
        volumeTicks.push(endingTick + 1);
      };
    };
  };

  // TRIM DOWN THE LENGTH
  if (timeTicks.length > 10) timeTicks = timeTicks.filter(tick => tick % 2 === 0);
  // TRIM DOWN THE LENGTH
  if (volumeTicks.length > 10) volumeTicks = volumeTicks.filter(tick => tick % 2 === 0);

  // Keep volume-time axes at -1s globally while preserving the 1:1 tick count.
  if (timeLeadIn) timeTicks = timeTicks.map(tick => tick - 1);

  return {
    volumeTimePoints,
    vtcTicks: {
      xTicks: timeTicks,
      yTicks: volumeTicks
    },
    ranges: {
      volumeRange: [volumeTicks.at(0) ?? 0, volumeTicks.at(-1) ?? 10],
      timeRange: [timeTicks.at(0) ?? 0, timeTicks.at(-1) ?? 15],
    }
  }
};

/**
 * @param {*} series - FVC | PEF Series
 */
export const tidyFlowVolumeCurve = (series) => {
  if (!series?.flowVolumeCurve) return [];

  let backendFlowVolumeCurveData;
  try {
    backendFlowVolumeCurveData = JSON.parse(atob(series.flowVolumeCurve));
  } catch {
    console.log("Unable to parse backend flow volume curve data");
  };

  let containsFvPointsCurve = !!series?.fvPointsCurve;
  const decodedFvPointsCurve = containsFvPointsCurve ? atob(series.fvPointsCurve) : atob(series.flowVolumeCurve);

  let fvPointsJson;
  try {
    fvPointsJson = JSON.parse(decodedFvPointsCurve);
    if (containsFvPointsCurve) { // Copy the MIR data to our conventionally named properties for fvPointsCurve
      fvPointsJson.points.forEach(point => {
        point.volume = point.volume_L
        point.flow = point.flow_Ls
      });
    }
  } catch {
    console.log('ERROR PARSING COMPRESSED CURVE JSON - ATTEMPTING FALLBACK');
    containsFvPointsCurve = false;
    try {
      fvPointsJson = backendFlowVolumeCurveData;
    } catch (e) {
      console.log(e);
      return [];
    }
  };

  if (!fvPointsJson?.points || fvPointsJson?.points?.length <= 0) return [];

  let firstTimestamp = fvPointsJson.points[0]?.timestamp;
  let exhalePoints = [{ flow: 0, volume: 0, timestamp: 0 }], inhalePoints = [];
  let minVolume = 0, maxVolume = -Infinity;
  let minFlow = 0, maxFlow = -Infinity;
  let endInhale = false, hitPef = false;

  if (containsFvPointsCurve) {
    fvPointsJson.points.forEach(point => {
      const { flow, volume } = point;
      if (hitPef && flow === 0) endInhale = true;

      if (flow > 0 && volume >= exhalePoints.at(-1).volume && inhalePoints.length === 0) {
        if (!endInhale) {
          minVolume = Math.min(minVolume, volume);
          maxVolume = Math.max(maxVolume, volume);
          minFlow = Math.min(minFlow, flow);
          maxFlow = Math.max(maxFlow, flow);
          if (flow > series.pef - 0.2) hitPef = true;

          exhalePoints.push({ flow, volume })
        } else {
          exhalePoints.push({ flow: 0, volume });
        }
      } else if (flow < 0) {
        if (inhalePoints.length === 0 || volume <= inhalePoints.at(-1).volume) {
          minFlow = Math.min(flow, minFlow);
          minVolume = Math.min(volume, minVolume);

          inhalePoints.push({ flow, volume });
        }
      };
    });
  } else {
    fvPointsJson.points.forEach((point) => {
      let { flow, volume, timestamp } = point;
      if (hitPef && flow === 0) endInhale = true;

      if (flow > 10000 || volume > 100000) console.log("error in point data - please check this curve in DB");
      else {
        if (flow > 0 && volume >= exhalePoints.at(-1).volume && inhalePoints.length === 0) {
          if (!endInhale) {
            if (series.pef && ((flow / 100) > (series.pef + 0.1))) flow = flow / 10;
            if (flow > maxFlow) maxFlow = flow;
            if (volume > maxVolume) maxVolume = volume;
            if (flow / 100 > series.pef - 0.2) hitPef = true;
            exhalePoints.push({ flow: (flow / 100), volume: (volume / 1000), timestamp: timestamp - firstTimestamp });
          } else {
            exhalePoints.push({ flow: 0, volume: (point.volume / 1000), timestamp: point.timestamp - firstTimestamp });
          }
        }
        else if (flow < 0) { // INHALING - in Millilitres
          if (inhalePoints.length === 0 || (volume / 1000) <= inhalePoints.at(-1).volume) {
            if (flow < minFlow) minFlow = flow;
            if (volume < minVolume) minVolume = volume;
            inhalePoints.push({ flow: (flow / 1000), volume: (volume / 1000), timestamp: timestamp - firstTimestamp });
          }
        };
      };
    });
  };

  // APPEND A 0 FLOW POINT TO THE END OF THE INHALE - IF NO INHALE POINTS, USE LAST EXHALE POINTS
  let highestVolume = series.fvc;
  if (series.pftType !== 'pef') {
    const lastInhaleVolume = inhalePoints.at(-1)?.volume ?? exhalePoints.at(-1)?.volume;
    const targetVolume = (series.fvc > 0 && series.fivc > 0 && (series.fvc - series.fivc) <= lastInhaleVolume)
      ? series.fvc - series.fivc
      : lastInhaleVolume;
    inhalePoints.push({ flow: 0, volume: targetVolume });
    const lastExhalePointVolume = exhalePoints.at(-1)?.volume ?? 0;
    highestVolume = Math.max(series.fvc, lastExhalePointVolume);
  }

  // MAKE SURE FLOW VOLUME CURVE TOUCHES FVC VALUE
  if (series.fvc > 0 && series.pftType !== 'pef') {
    inhalePoints.unshift({ flow: 0, volume: highestVolume });
    exhalePoints.push({ flow: 0, volume: highestVolume });
  };

  // CREATE VOLUME TICKS FOR X-AXIS
  let volumeTicks = [];
  const startingVolumeTick = containsFvPointsCurve ? Math.ceil(minVolume) - 1 : Math.ceil(minVolume === 0 ? minVolume : minVolume / 1000) - 1;
  const endingVolumeTick = Math.floor(containsFvPointsCurve ? maxVolume : maxVolume / 1000) + 2;

  for (let i = startingVolumeTick; i <= endingVolumeTick; i += 1) {
    volumeTicks.push(i);
  };
  // TRIM DOWN THE LENGTH
  // if (volumeTicks.length > 10) volumeTicks = volumeTicks.filter(tick => tick % 2 === 0);

  // CREATE FLOW TICKS
  let flowTicks = [];
  const startingFlowTick = series.pftType === 'pef' ? Math.ceil(containsFvPointsCurve ? minFlow : minFlow / 1000) : Math.ceil(containsFvPointsCurve ? minFlow : minFlow / 1000) - 2;
  const endingFlowTick = Math.floor(containsFvPointsCurve ? maxFlow : maxFlow / 100) + 3;

  if (inhalePoints.length < 10 && series.pftType !== 'pef') {
    for (let i = -6; i <= endingFlowTick; i += 1) {
      if (i % 2 === 0) flowTicks.push(i);
    };
  } else {
    for (let i = startingFlowTick; i <= endingFlowTick; i += 1) {
      if (i % 2 === 0) flowTicks.push(i);
    };
  };

  // CREATE SAME AMOUNT OF TICKS FOR EACH AXIS
  if (volumeTicks.length !== flowTicks.length) {
    if (volumeTicks.length > flowTicks.length) {
      // add to time ticks
      let tickDifference = volumeTicks.length - flowTicks.length;
      for (let i = 0; i < tickDifference; i += 1) {
        let endingTick = flowTicks.at(-1);
        flowTicks.push(endingTick + 2);
      };
    } else if (volumeTicks.length < flowTicks.length) {
      // add to volume ticks
      let tickDifference = flowTicks.length - volumeTicks.length;
      for (let i = 0; i < tickDifference; i += 1) {
        let endingTick = volumeTicks.at(-1);
        volumeTicks.push(endingTick + 1);
      };
    };
  };

  // TRIM DOWN THE LENGTH
  // if (flowTicks.length > 17) flowTicks = flowTicks.filter((tick, i) => i % 3 === 0);
  // else if (flowTicks.length > 12) flowTicks = flowTicks.filter((tick, i) => i % 2 === 0);

  return {
    exhalePoints,
    inhalePoints,
    fvc: series.fvc,
    fvcTicks: {
      xTicks: volumeTicks,
      yTicks: flowTicks
    },
    ranges: {
      volumeRange: [volumeTicks.at(0) ?? -1, volumeTicks.at(-1) ?? 10],
      flowRange: [flowTicks.at(0) ?? -8, flowTicks.at(-1) ?? 15],
    }
  }
};

export const drawZScoreCanvas = (ctx, fvcZ, fev1Z, fev1FvcZ, segmentLength, width, height) => {
  ctx.clearRect(0, 0, width, height);
  ctx.font = '12px serif';

  const fvcPoint = (clamp(-5, 3, fvcZ) + 5) * segmentLength;
  const fev1Point = (clamp(-5, 3, fev1Z) + 5) * segmentLength;
  const fev1FvcPoint = (clamp(-5, 3, fev1FvcZ) + 5) * segmentLength;

  for (let i = 0; i < 3; i += 1) {
    let yPos, testText, testText2ndLine;
    if (i === 0) {
      yPos = 20;
      testText = "FVC";
    }
    else if (i === 1) {
      yPos = 50;
      testText = "FEV1";
    }
    else {
      yPos = 80;
      testText = "FEV1";
      testText2ndLine = "/FVC"
    }

    ctx.fillStyle = '#b30000';
    ctx.fillRect(segmentLength * 0, yPos, segmentLength * 1 - 2, 8);

    ctx.fillStyle = 'white';
    ctx.fillRect(segmentLength * 1 - 2, yPos, 2, 8);

    ctx.fillStyle = '#e3e31c';
    ctx.fillRect(segmentLength * 1, yPos, segmentLength * 1 - 2, 8);

    ctx.fillStyle = 'white';
    ctx.fillRect(segmentLength * 2 - 2, yPos, 1, 8);

    ctx.fillStyle = '#e3e31c';
    ctx.fillRect(segmentLength * 2, yPos, segmentLength * 1 - 2, 8);

    ctx.fillStyle = 'white';
    ctx.fillRect(segmentLength * 3 - 2, yPos, 1, 8);

    ctx.fillStyle = '#e3e31c';
    ctx.fillRect(segmentLength * 3, yPos, segmentLength * 0.355, 8);

    ctx.fillStyle = '#00802b';
    ctx.fillRect((segmentLength * 3) + (segmentLength * 0.355), yPos, segmentLength * 0.645 - 2, 8);

    ctx.fillStyle = 'white';
    ctx.fillRect(segmentLength * 4 - 2, yPos, 1, 8);

    ctx.fillStyle = '#00802b';
    ctx.fillRect(segmentLength * 4, yPos, segmentLength * 1 - 2, 8);

    ctx.fillStyle = 'white';
    ctx.fillRect(segmentLength * 5 - 2, yPos, 1, 8);

    ctx.fillStyle = '#00802b';
    ctx.fillRect(segmentLength * 5, yPos, segmentLength * 1 - 2, 8);

    ctx.fillStyle = 'white';
    ctx.fillRect(segmentLength * 6 - 2, yPos, 1, 8);

    ctx.fillStyle = '#00802b';
    ctx.fillRect(segmentLength * 6, yPos, segmentLength * 1 - 2, 8);

    ctx.fillStyle = 'white';
    ctx.fillRect(segmentLength * 7 - 2, yPos, 1, 8);

    ctx.fillStyle = '#00802b';
    ctx.fillRect(segmentLength * 7, yPos, segmentLength * 1, 8);

    ctx.fillStyle = 'black';
    ctx.fillRect((segmentLength * 3) + (segmentLength * 0.355), yPos - 4, 2, 16);
    ctx.fillRect(segmentLength * 5 - 2, yPos - 4, 2, 16);
    ctx.fillRect((segmentLength * 6) + (segmentLength * 0.645), yPos - 4, 2, 16);

    if (testText2ndLine) {
      ctx.fillText(testText, segmentLength * 8 + 2, yPos + 3);
      ctx.fillText(testText2ndLine, segmentLength * 8 + 2, yPos + 13);
    }
    else ctx.fillText(testText, segmentLength * 8 + 2, yPos + 8);
  }

  ctx.fillText("LLN", segmentLength * 3 + 4, 8);
  ctx.fillText("Pred", segmentLength * 4 + segmentLength / 2 + 4, 8);
  ctx.fillText("-5", 0, 100);
  ctx.fillText("-4", segmentLength * 0.8, 100);
  ctx.fillText("-3", segmentLength * 1.8, 100);
  ctx.fillText("-2", segmentLength * 2.8, 100);
  ctx.fillText("-1", segmentLength * 3.8, 100);
  ctx.fillText("0", segmentLength * 4.88, 100);
  ctx.fillText("1", segmentLength * 5.88, 100);
  ctx.fillText("2", segmentLength * 6.88, 100);
  ctx.fillText("3", segmentLength * 7.8, 100);

  ctx.beginPath();
  ctx.moveTo(fvcPoint, 19);
  ctx.lineTo(fvcPoint + 5, 12);
  ctx.lineTo(fvcPoint - 5, 12);
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(fev1Point, 49);
  ctx.lineTo(fev1Point + 5, 42);
  ctx.lineTo(fev1Point - 5, 42);
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(fev1FvcPoint, 79);
  ctx.lineTo(fev1FvcPoint + 5, 72);
  ctx.lineTo(fev1FvcPoint - 5, 72);
  ctx.fill();
};

export const drawSvcZScoreCanvas = (ctx, vcZ, segmentLength, width, height) => {
  ctx.clearRect(0, 0, width, height);
  ctx.font = '12px serif';

  const vcPoint = (clamp(-5, 3, vcZ) + 5) * segmentLength;

  let yPos = 20;

  ctx.fillStyle = '#b30000';
  ctx.fillRect(segmentLength * 0, yPos, segmentLength * 1 - 2, 8);

  ctx.fillStyle = 'white';
  ctx.fillRect(segmentLength * 1 - 2, yPos, 2, 8);

  ctx.fillStyle = '#e3e31c';
  ctx.fillRect(segmentLength * 1, yPos, segmentLength * 1 - 2, 8);

  ctx.fillStyle = 'white';
  ctx.fillRect(segmentLength * 2 - 2, yPos, 1, 8);

  ctx.fillStyle = '#e3e31c';
  ctx.fillRect(segmentLength * 2, yPos, segmentLength * 1 - 2, 8);

  ctx.fillStyle = 'white';
  ctx.fillRect(segmentLength * 3 - 2, yPos, 1, 8);

  ctx.fillStyle = '#e3e31c';
  ctx.fillRect(segmentLength * 3, yPos, segmentLength * 0.355, 8);

  ctx.fillStyle = '#00802b';
  ctx.fillRect((segmentLength * 3) + (segmentLength * 0.355), yPos, segmentLength * 0.645 - 2, 8);

  ctx.fillStyle = 'white';
  ctx.fillRect(segmentLength * 4 - 2, yPos, 1, 8);

  ctx.fillStyle = '#00802b';
  ctx.fillRect(segmentLength * 4, yPos, segmentLength * 1 - 2, 8);

  ctx.fillStyle = 'white';
  ctx.fillRect(segmentLength * 5 - 2, yPos, 1, 8);

  ctx.fillStyle = '#00802b';
  ctx.fillRect(segmentLength * 5, yPos, segmentLength * 1 - 2, 8);

  ctx.fillStyle = 'white';
  ctx.fillRect(segmentLength * 6 - 2, yPos, 1, 8);

  ctx.fillStyle = '#00802b';
  ctx.fillRect(segmentLength * 6, yPos, segmentLength * 1 - 2, 8);

  ctx.fillStyle = 'white';
  ctx.fillRect(segmentLength * 7 - 2, yPos, 1, 8);

  ctx.fillStyle = '#00802b';
  ctx.fillRect(segmentLength * 7, yPos, segmentLength * 1, 8);

  ctx.fillStyle = 'black';
  ctx.fillRect((segmentLength * 3) + (segmentLength * 0.355), yPos - 4, 2, 16);
  ctx.fillRect(segmentLength * 5 - 2, yPos - 4, 2, 16);
  ctx.fillRect((segmentLength * 6) + (segmentLength * 0.645), yPos - 4, 2, 16);

  ctx.fillText("SVC", segmentLength * 8 + 2, yPos + 8);

  ctx.fillText("LLN", segmentLength * 3 + 4, 8);
  ctx.fillText("Pred", segmentLength * 4 + segmentLength / 2 + 4, 8);
  ctx.fillText("-5", 0, 40);
  ctx.fillText("-4", segmentLength * 0.8, 40);
  ctx.fillText("-3", segmentLength * 1.8, 40);
  ctx.fillText("-2", segmentLength * 2.8, 40);
  ctx.fillText("-1", segmentLength * 3.8, 40);
  ctx.fillText("0", segmentLength * 4.88, 40);
  ctx.fillText("1", segmentLength * 5.88, 40);
  ctx.fillText("2", segmentLength * 6.88, 40);
  ctx.fillText("3", segmentLength * 7.8, 40);

  ctx.beginPath();
  ctx.moveTo(vcPoint, 19);
  ctx.lineTo(vcPoint + 5, 12);
  ctx.lineTo(vcPoint - 5, 12);
  ctx.fill();
};

export const drawPefZScoreCanvas = (ctx, pefZ, segmentLength, width, height) => {
  ctx.clearRect(0, 0, width, height);
  ctx.font = '12px serif';

  const pefPoint = (clamp(-5, 3, pefZ) + 5) * segmentLength;

  const yPos = 20;
  const testText = "PEF";

  ctx.fillStyle = '#b30000';
  ctx.fillRect(segmentLength * 0, yPos, segmentLength * 1 - 2, 8);

  ctx.fillStyle = 'white';
  ctx.fillRect(segmentLength * 1 - 2, yPos, 2, 8);

  ctx.fillStyle = '#e3e31c';
  ctx.fillRect(segmentLength * 1, yPos, segmentLength * 1 - 2, 8);

  ctx.fillStyle = 'white';
  ctx.fillRect(segmentLength * 2 - 2, yPos, 1, 8);

  ctx.fillStyle = '#e3e31c';
  ctx.fillRect(segmentLength * 2, yPos, segmentLength * 1 - 2, 8);

  ctx.fillStyle = 'white';
  ctx.fillRect(segmentLength * 3 - 2, yPos, 1, 8);

  ctx.fillStyle = '#e3e31c';
  ctx.fillRect(segmentLength * 3, yPos, segmentLength * 0.355, 8);

  ctx.fillStyle = '#00802b';
  ctx.fillRect((segmentLength * 3) + (segmentLength * 0.355), yPos, segmentLength * 0.645 - 2, 8);

  ctx.fillStyle = 'white';
  ctx.fillRect(segmentLength * 4 - 2, yPos, 1, 8);

  ctx.fillStyle = '#00802b';
  ctx.fillRect(segmentLength * 4, yPos, segmentLength * 1 - 2, 8);

  ctx.fillStyle = 'white';
  ctx.fillRect(segmentLength * 5 - 2, yPos, 1, 8);

  ctx.fillStyle = '#00802b';
  ctx.fillRect(segmentLength * 5, yPos, segmentLength * 1 - 2, 8);

  ctx.fillStyle = 'white';
  ctx.fillRect(segmentLength * 6 - 2, yPos, 1, 8);

  ctx.fillStyle = '#00802b';
  ctx.fillRect(segmentLength * 6, yPos, segmentLength * 1 - 2, 8);

  ctx.fillStyle = 'white';
  ctx.fillRect(segmentLength * 7 - 2, yPos, 1, 8);

  ctx.fillStyle = '#00802b';
  ctx.fillRect(segmentLength * 7, yPos, segmentLength * 1, 8);

  ctx.fillStyle = 'black';
  ctx.fillRect((segmentLength * 3) + (segmentLength * 0.355), yPos - 4, 2, 16);
  ctx.fillRect(segmentLength * 5 - 2, yPos - 4, 2, 16);
  ctx.fillRect((segmentLength * 6) + (segmentLength * 0.645), yPos - 4, 2, 16);

  ctx.fillText(testText, segmentLength * 8 + 2, yPos + 8);

  ctx.fillText("LLN", segmentLength * 3 + 4, 8);
  ctx.fillText("Pred", segmentLength * 4 + segmentLength / 2 + 4, 8);
  ctx.fillText("-5", 0, 40);
  ctx.fillText("-4", segmentLength * 0.8, 40);
  ctx.fillText("-3", segmentLength * 1.8, 40);
  ctx.fillText("-2", segmentLength * 2.8, 40);
  ctx.fillText("-1", segmentLength * 3.8, 40);
  ctx.fillText("0", segmentLength * 4.88, 40);
  ctx.fillText("1", segmentLength * 5.88, 40);
  ctx.fillText("2", segmentLength * 6.88, 40);
  ctx.fillText("3", segmentLength * 7.8, 40);

  ctx.beginPath();
  ctx.moveTo(pefPoint, 19);
  ctx.lineTo(pefPoint + 5, 12);
  ctx.lineTo(pefPoint - 5, 12);
  ctx.fill();
};

export const formatProviderDateFormatForMomentJs = (dateFormat = 'yyyy/mm/dd') => dateFormat.replace(/\//g, " ").toUpperCase();

/**
 * @param {number} unixTime
 * @param {Provider} provider
 * @returns {string}
 */
export const formatUnixTimestampForProvider = (unixTime, provider) => {
  const providerDateFormat = formatProviderDateFormatForMomentJs(provider.dateFormat);
  const result = moment.parseZone(unixTime)
    .add(1, 'days')
    .format(providerDateFormat)
    .split(" ")
    .join("/");
  return result;
};

/**
 *
 * @param {any[]} chart
 * @param {import('../Types/ChartTypes').ChartRangeOptions; label?: string; } range
 * @param {number} page
 * @returns
 */
export const cutRange = (chart, range, page) => {
  let day = 0;
  switch (range) {
    case 'oneWeek':
      day = chart.length - (7 * page);
      return chart.slice(day >= 0 ? day : 0, chart.length - (7 * (page - 1)));
    case 'twoWeeks':
      day = chart.length - (14 * page);
      return chart.slice(day >= 0 ? day : 0, chart.length - (14 * (page - 1)));
    case 'oneMonth':
      day = chart.length - (30 * page);
      return chart.slice(day >= 0 ? day : 0, chart.length - (30 * (page - 1)));
    case 'oneQuarter':
      day = chart.length - (90 * page);
      return chart.slice(day >= 0 ? day : 0, chart.length - (90 * (page - 1)));
    case 'sixMonths':
      day = chart.length - (180 * page);
      return chart.slice(day >= 0 ? day : 0, chart.length - (180 * (page - 1)));
    case 'oneYear':
      day = chart.length - (365 * page);
      return chart.slice(day >= 0 ? day : 0, chart.length - (365 * (page - 1)));
    default:
      return chart;
  }
}

/**
 *
 * @param {import('../Types/ChartTypes').ChartRangeOptions} rangeLength
 * @returns {number}
 */
export const getViewingChartLength = (rangeLength) => {
  switch (rangeLength) {
    case 'oneWeek':
      return 7;
    case 'twoWeeks':
      return 14;
    case 'oneMonth':
      return 30;
    case 'oneQuarter':
      return 90;
    case 'sixMonths':
      return 180;
    case 'oneYear':
      return 365;
    default:
      return 365;
  }
};

/**
 * @param {string} showTrendGraphOnPdf
 * @returns {import('../Types/ChartTypes').ChartRangeOptions}
 */
export const generateHiddenChartRangeLength = (showTrendGraphOnPdf) => {
  switch (showTrendGraphOnPdf) {
    case '12months':
      return 'oneYear';
    case '6months':
      return 'sixMonths';
    case '3months':
      return 'oneQuarter';
    default:
      return 'oneQuarter';
  };
};

/**
 * @param {string} showTrendGraphOnPdf
 * @returns {string}
 */
export const generateChartLengthString = (showTrendGraphOnPdf) => {
  switch (showTrendGraphOnPdf) {
    case '12months':
      return 'one year';
    case '6months':
      return '6 months';
    case '3months':
      return '3 months';
    default:
      return '';
  };
};

export const generateChartRangeOptions = (translationText = {}) => {
  return {
    oneWeek: { value: 'oneWeek', label: translationText?.week ?? 'One Week' },
    twoWeeks: { value: 'twoWeeks', label: translationText?.twoWeeks ?? 'Two Weeks' },
    oneMonth: { value: 'oneMonth', label: translationText?.oneMonth ?? 'One Month' },
    oneQuarter: { value: 'oneQuarter', label: translationText?.quarter ?? 'One Quarter' },
    sixMonths: { value: 'sixMonths', label: translationText?.sixMonths ?? "Six Months" },
    oneYear: { value: 'oneYear', label: translationText?.oneYear ?? 'One Year' },
  }
}

export const getChartProps = (chartType, translationText, ranges) => {
  switch (chartType) {
    case 'fv':
      return {
        xAxisDomain: ranges.current.volumeRange,
        xAxisDataKey: 'volume',
        xAxisLabel: `${translationText.volume} (L)`,
        yAxisDomain: ranges.current.flowRange,
        yAxisLabel: `${translationText.flow} (L/s)`,
        dataKey: 'flow'
      }
    case 'vt':
      return {
        xAxisDomain: ranges.current.timeRange,
        xAxisDataKey: 'timestamp',
        xAxisLabel: `${translationText.time} (s)`,
        yAxisDomain: ranges.current.volumeRange,
        yAxisLabel: `${translationText.volume} (L)`,
        dataKey: 'volume'
      }
    case 'svc':
      return {
        xAxisDomain: ranges.current.timeRange,
        xAxisDataKey: 'time',
        xAxisLabel: `${translationText.time} (s)`,
        yAxisDomain: ranges.current.volumeRange,
        yAxisLabel: `${translationText.volume} (L)`,
        dataKey: 'volume'
      }
    default:
      return {};
  }
};

export const drawCoughVasScoreCanvas = (ctx, coughVasValue, width, height) => {
  ctx.clearRect(0, 0, width, height);
  ctx.font = '12px Nunito';

  const coughVasPoint = (clamp(0, 600, coughVasValue * 6));
  // horizontal line
  ctx.fillStyle = 'black';
  ctx.fillRect(0, 20, 600, 2);
  // beginning thumb
  ctx.beginPath();
  ctx.moveTo(5, 8);
  ctx.lineTo(5, 35);
  ctx.lineTo(0, 35);
  ctx.lineTo(0, 8);
  ctx.fill();
  // ending thumb
  ctx.beginPath();
  ctx.moveTo(595, 8);
  ctx.lineTo(595, 35);
  ctx.lineTo(600, 35);
  ctx.lineTo(600, 8);
  ctx.fill();
  // X marker
  ctx.beginPath();
  ctx.moveTo(coughVasPoint - 12, 9);
  ctx.lineTo(coughVasPoint + 12, 34);
  ctx.stroke();
  ctx.moveTo(coughVasPoint + 12, 9);
  ctx.lineTo(coughVasPoint - 12, 34);
  ctx.stroke();

  ctx.fillText("No Cough", 0, 50);
  ctx.fillText("Worst Cough", 528, 50);
  ctx.fillText("Ever", 550, 65);
};

export const drawVerticalCoughVasScoreCanvas = (ctx, coughVasValue, width, height) => {
  ctx.clearRect(0, 0, width, height);
  ctx.font = '12px Nunito';

  const coughVasPoint = (clamp(0, 600, coughVasValue * 6));
  // horizontal line
  ctx.fillStyle = 'black';
  ctx.fillRect(20, 0, 2, 600);
  // beginning thumb
  ctx.beginPath();
  ctx.moveTo(8, 5);
  ctx.lineTo(35, 5);
  ctx.lineTo(35, 0);
  ctx.lineTo(8, 0);
  ctx.fill();
  // ending thumb
  ctx.beginPath();
  ctx.moveTo(8, 595);
  ctx.lineTo(35, 595);
  ctx.lineTo(35, 600);
  ctx.lineTo(8, 600);
  ctx.fill();
  // X marker
  ctx.beginPath();
  ctx.moveTo(8, coughVasPoint - 12);
  ctx.lineTo(34, coughVasPoint + 12);
  ctx.stroke();
  ctx.moveTo(8, coughVasPoint + 12);
  ctx.lineTo(34, coughVasPoint - 12);
  ctx.stroke();

  ctx.fillText("No Cough", 40, 597);
  ctx.fillText("Worst Cough", 40, 10);
  ctx.fillText("Ever", 60, 25);
};

export const drawVasScoreCanvas = (ctx, coughVasValue, width, height, leftText, rightText) => {
  ctx.clearRect(0, 0, width, height);
  ctx.font = '12px Nunito';

  const padding = 10;
  const lineLength = width - padding * 2;

  const coughVasPoint = clamp(0, lineLength, coughVasValue * (lineLength / 100));

  // horizontal line
  ctx.fillStyle = 'black';
  ctx.fillRect(padding, 20, lineLength, 2);

  // thumbs
  ctx.beginPath();
  ctx.moveTo(padding + 5, 8);
  ctx.lineTo(padding + 5, 35);
  ctx.lineTo(padding, 35);
  ctx.lineTo(padding, 8);
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(padding + lineLength - 5, 8);
  ctx.lineTo(padding + lineLength - 5, 35);
  ctx.lineTo(padding + lineLength, 35);
  ctx.lineTo(padding + lineLength, 8);
  ctx.fill();

  // X marker
  ctx.beginPath();
  ctx.moveTo(padding + coughVasPoint - 12, 9);
  ctx.lineTo(padding + coughVasPoint + 12, 34);
  ctx.stroke();
  ctx.moveTo(padding + coughVasPoint + 12, 9);
  ctx.lineTo(padding + coughVasPoint - 12, 34);
  ctx.stroke();

  // Draw labels with ellipsis if needed
  const maxLabelWidth = lineLength / 2 - padding;
  const trimText = (text, maxWidth) => {
    while (ctx.measureText(text).width > maxWidth) {
      if (text.length <= 1) return '';
      text = text.slice(0, -1);
    }
    return text;
  };

  let leftLabel = leftText;
  let rightLabel = rightText;

  if (ctx.measureText(leftLabel).width > maxLabelWidth) {
    leftLabel = trimText(leftLabel, maxLabelWidth - ctx.measureText('...').width) + '...';
  }

  if (ctx.measureText(rightLabel).width > maxLabelWidth) {
    rightLabel = trimText(rightLabel, maxLabelWidth - ctx.measureText('...').width) + '...';
  }

  ctx.fillText(leftLabel, padding, 50);
  const rightTextWidth = ctx.measureText(rightLabel).width;
  ctx.fillText(rightLabel, padding + lineLength - rightTextWidth, 50);
};

const calculateNiceAxisBounds = (dataMin, dataMax, targetTickCount = 6) => {
  const range = dataMax - dataMin || 1;
  const roughStep = range / (targetTickCount - 1);

  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const residual = roughStep / magnitude;

  let niceStep;
  if (residual <= 1.5) niceStep = magnitude;
  else if (residual <= 3) niceStep = 2 * magnitude;
  else if (residual <= 7) niceStep = 5 * magnitude;
  else niceStep = 10 * magnitude;

  const niceMin = Math.floor(dataMin / niceStep) * niceStep;
  const niceMax = Math.ceil(dataMax / niceStep) * niceStep;

  const ticks = [];
  for (let v = niceMin; v <= niceMax + niceStep * 0.5; v += niceStep) {
    ticks.push(Math.round(v * 1000) / 1000);
  }

  return { min: niceMin, max: niceMax, ticks, step: niceStep };
};


/**
 * Merges R and X arrays by frequency into a single array of data points.
 *
 * @param {Object} frequencyCurve - Backend frequency curve data
 * @param {Array<{frequency: number, value: number, unit: string}>} frequencyCurve.R - Resistance values
 * @param {Array<{frequency: number, value: number, unit: string}>} frequencyCurve.X - Reactance values
 * @returns {Object} data with points, ranges, and ticks
 */
export const tidyFrequencyCurve = (frequencyCurve) => {
  if (!frequencyCurve || (!frequencyCurve.R?.length && !frequencyCurve.X?.length)) {
    return null;
  }

  const frequencyMap = new Map();

  if (frequencyCurve.R) {
    frequencyCurve.R.forEach(point => {
      frequencyMap.set(point.frequency, {
        frequency: point.frequency,
        R: point.value,
        X: null,
        unit: point.unit
      });
    });
  }

  if (frequencyCurve.X) {
    frequencyCurve.X.forEach(point => {
      if (frequencyMap.has(point.frequency)) {
        frequencyMap.get(point.frequency).X = point.value;
      } else {
        frequencyMap.set(point.frequency, {
          frequency: point.frequency,
          R: null,
          X: point.value,
          unit: point.unit
        });
      }
    });
  }

  const points = Array.from(frequencyMap.values())
    .sort((a, b) => a.frequency - b.frequency);

  if (points.length === 0) return null;

  const rValues = points.map(p => p.R).filter(v => v !== null);
  const xValues = points.map(p => p.X).filter(v => v !== null);
  const allValues = [...rValues, ...xValues];

  const dataMinValue = Math.min(...allValues);
  const dataMaxValue = Math.max(...allValues);

  // Y-axis: hardcoded range until dynamic scaling specs are provided
  const valueRange = [-5, 10];
  const yTicks = [-5, -2.5, 0, 2.5, 5, 7.5, 10];

  // X-axis: fixed range 0-40 Hz w/ 5 Hz intervals
  const frequencyTicks = [0, 5, 10, 15, 20, 25, 30, 35, 40];

  const unit = points.find(p => p.unit)?.unit || 'cmH2O/(L/s)';

  return {
    points,
    unit,
    ranges: {
      frequencyRange: [0, 40],
      valueRange,
    },
    ticks: {
      xTicks: frequencyTicks,
      yTicks,
    }
  };
};

export const drawVerticalVasScoreCanvas = (ctx, coughVasValue, width, height, leftText, rightText) => {
  ctx.clearRect(0, 0, width, height);
  ctx.font = '12px Nunito';

  const padding = 10;
  const lineLength = height - padding * 2;
  const vasX = 20; // x-position of vertical bar
  const coughVasPoint = clamp(0, lineLength, coughVasValue * (lineLength / 100));

  ctx.fillStyle = 'black';
  ctx.fillRect(vasX, padding, 2, lineLength);

  // Beginning thumb (top)
  ctx.beginPath();
  ctx.moveTo(vasX - 12, padding);
  ctx.lineTo(vasX + 15, padding);
  ctx.lineTo(vasX + 15, padding - 5);
  ctx.lineTo(vasX - 12, padding - 5);
  ctx.fill();

  // Ending thumb (bottom)
  ctx.beginPath();
  ctx.moveTo(vasX - 12, padding + lineLength);
  ctx.lineTo(vasX + 15, padding + lineLength);
  ctx.lineTo(vasX + 15, padding + lineLength + 5);
  ctx.lineTo(vasX - 12, padding + lineLength + 5);
  ctx.fill();

  // X marker
  ctx.beginPath();
  ctx.moveTo(vasX - 12, padding + coughVasPoint - 12);
  ctx.lineTo(vasX + 12, padding + coughVasPoint + 12);
  ctx.stroke();
  ctx.moveTo(vasX - 12, padding + coughVasPoint + 12);
  ctx.lineTo(vasX + 12, padding + coughVasPoint - 12);
  ctx.stroke();

  // Draw labels, trimming if needed
  const maxLabelWidth = width - (vasX + 20 + padding);
  const trimText = (text, maxWidth) => {
    while (ctx.measureText(text).width > maxWidth) {
      if (text.length <= 1) return '';
      text = text.slice(0, -1);
    }
    return text;
  };

  let bottomLabel = leftText;
  let topLabel = rightText;

  if (ctx.measureText(bottomLabel).width > maxLabelWidth) {
    bottomLabel = trimText(bottomLabel, maxLabelWidth - ctx.measureText('...').width) + '...';
  }

  if (ctx.measureText(topLabel).width > maxLabelWidth) {
    topLabel = trimText(topLabel, maxLabelWidth - ctx.measureText('...').width) + '...';
  }

  // Draw bottom label (leftText)
  ctx.textBaseline = 'bottom';
  ctx.fillText(bottomLabel, vasX + 20, height - padding);

  // Draw top label (rightText)
  ctx.textBaseline = 'top';
  ctx.fillText(topLabel, vasX + 20, padding);
};
